import { cn } from '../../utils/utils';

export const bilingualTextFieldClasses = cn(
    'py-2 px-4 rounded-xl text-foreground bg-transparent w-full',
    'border border-foreground transition-all outline-none focus:border-primary',
);

interface BilingualFieldProps {
    label: string;
    placeholder?: string;
    hint?: string;
    /** Renders textareas instead of single-line inputs (longer flow texts). */
    multiline?: boolean;
    disabled?: boolean;
    value: { de: string, en: string };
    onChange: (lang: 'de' | 'en', value: string) => void;
}

/**
 * One customer-facing text entry as a DE/EN field pair — the same
 * two-fields-per-entry pattern as object create/edit
 * (`AdditionalServicesSection` langNames rows), so the end-user app can serve
 * each visitor their own language instead of a single mixed-language string.
 *
 * Extracted from `pages/account/CheckInCheckOutSection.tsx` when the ideas-card
 * section needed the same control (shared building block rule).
 */
const BilingualField = ({
    label,
    placeholder,
    hint,
    multiline = false,
    disabled = false,
    value,
    onChange,
}: Readonly<BilingualFieldProps>) => (
    <div className={'flex flex-col gap-1.5'}>
        <label className={'text-sm text-foreground/70 font-medium'}>{label}</label>
        {(['de', 'en'] as const).map((lang) => (
            <div key={lang} className={'flex items-start gap-2'}>
                <span className={'text-xs text-foreground/40 w-6 mt-3 uppercase'}>{lang}</span>
                {multiline ? (
                    <textarea
                        className={cn(bilingualTextFieldClasses, 'min-h-[120px] resize-y')}
                        value={value[lang]}
                        onChange={(e) => onChange(lang, e.target.value)}
                        placeholder={placeholder}
                        disabled={disabled}
                    />
                ) : (
                    <input
                        type={'text'}
                        className={bilingualTextFieldClasses}
                        value={value[lang]}
                        onChange={(e) => onChange(lang, e.target.value)}
                        placeholder={placeholder}
                        disabled={disabled}
                    />
                )}
            </div>
        ))}
        {hint && <p className={'text-xs text-foreground/60'}>{hint}</p>}
    </div>
);

export default BilingualField;
